import {
  AuthStore,
  createHttpClient,
  createQueryClient,
  MediaRef,
  MediaResolver,
  TokenPair,
} from '@lb/domain'

import { env } from '../env'
import { reportError } from './telemetry'

export const queryClient = createQueryClient()

/**
 * Every failed read and write is reported, once, from here.
 *
 * The alternative is a `onError` on each of the ~40 hooks, which is forty
 * chances to forget — and the one that gets forgotten is always the interesting
 * one. React Query's caches expose exactly this seam, so the reporting lives
 * where the failures already converge.
 *
 * `ContractError` is the case this is really for: the app parses every response
 * with Zod at the boundary (§4.6), and that discipline is worth much less if a
 * shape mismatch in production is a console line nobody reads.
 *
 * `redact` in `telemetry.ts` decides what may leave — the payload here is an
 * error object that can hold a member's own data.
 */
queryClient.getQueryCache().subscribe((event) => {
  if (event.type === 'updated' && event.action.type === 'error') {
    reportError(event.action.error, { source: 'query' })
  }
})

queryClient.getMutationCache().subscribe((event) => {
  if (event.type === 'updated' && event.action.type === 'error') {
    reportError(event.action.error, { source: 'mutation' })
  }
})

/**
 * The transport, wired once.
 *
 * `AuthStore` needs a refresher and the refresher needs a client, so the client
 * is built first without auth and the store is handed a function that closes
 * over it. Refresh is the one call that must NOT carry an Authorization header
 * or retry on 401 — it is the thing that fixes a 401.
 */
const refreshClient = createHttpClient({ baseUrl: env.VITE_API_URL })

/**
 * The unauthenticated client, for the funnel.
 *
 * Separate from `http` on purpose: every checkout route is `@Public()`, and a
 * client carrying an Authorization header would attach one the moment a stale
 * token happened to be in storage — which is how a signup gets attributed to
 * whoever last used the browser. No auth, so no token, ever.
 */
export const publicHttp = createHttpClient({ baseUrl: env.VITE_API_URL })

export const auth = new AuthStore((refreshToken) =>
  refreshClient.post('/auth/refresh', { refreshToken }, { schema: TokenPair }),
)

export const http = createHttpClient({
  baseUrl: env.VITE_API_URL,
  auth,
  onSignOut: () => {
    // Server state belongs to the person who was signed in. Clearing it stops
    // the next member seeing a flash of the previous one's data.
    queryClient.clear()
  },
})

/**
 * Blocked until the backend opens media to members — see
 * ../laboussle-backend/docs/requests-from-webapp.md §2. The resolver is wired
 * anyway so `<AssetImage>` has one seam rather than every card growing a
 * placeholder branch of its own.
 */
export const media = new MediaResolver((id) => http.get(`/media/${id}`, { schema: MediaRef }))
